import { useState, type ReactNode } from 'react';
import { ApiError, carrierApi, type CarrierAccountRow, type CarrierChange, type CarrierKeyHealth, type CarrierSettingsView, type SettingChanged, type SettingSource } from '../../api/client';
import { Button, Card, Chip, Dialog, Field, LoadError, Loading, Notice, SelectField } from '../../components/ui';
import { shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → System → Carrier (1.12.12): Ahoi's settings, kept in Stitch instead of EasyPanel's
 * variables, and used at once (no redeploy). Secrets go in and never come back: the page only
 * knows whether the API token and the webhook secret are set. A setting saved here wins over
 * EasyPanel's variable; "Move from EasyPanel" copies the variables in, and the page then says
 * which ones can be deleted.
 *
 * Several carrier accounts (1.12.25): Ceexo's numbers are spread over more than one Ahoi account.
 * The bar on top lists them (the first is the one Stitch always had), adds, renames and removes
 * them, and says which one new numbers go to; the settings below are the chosen account's.
 */

const VAR: Record<string, string> = {
  apiKey: 'AHOI_API_KEY',
  apiUrl: 'AHOI_API_URL',
  callFlow: 'AHOI_CALL_FLOW_ID',
  trunk: 'PBX_NUMBER_TRUNK',
  writes: 'AHOI_WRITES',
  webhookSecret: 'AHOI_WEBHOOK_SECRET',
  webhookIps: 'AHOI_WEBHOOK_IPS'
};

function From({ source, setting, changed }: { source: SettingSource; setting: string; changed: SettingChanged | null }) {
  const text =
    source === 'stitch'
      ? `Saved in Stitch${changed ? ` · ${shortWhen(changed.at)}${changed.byName ? ` by ${changed.byName}` : ''}` : ''}`
      : source === 'easypanel'
        ? `From EasyPanel (${VAR[setting]})`
        : source === 'default'
          ? 'Ahoi’s own'
          : 'Not set';
  return <small className="muted" style={{ display: 'block' }}>{text}</small>;
}

/** The API token's age, its last test and when to rotate it (1.13.0, Teampace 3125, proposal 76). */
function KeyHealth({ h }: { h: CarrierKeyHealth }) {
  const age = h.ageDays === null ? null : h.ageDays === 0 ? 'saved today' : `${h.ageDays === 1 ? '1 day' : `${h.ageDays} days`} old`;
  const tested = !h.lastTest
    ? 'not tested since it was saved'
    : h.lastTest.ok
      ? `last tested ${shortWhen(h.lastTest.at)}, it worked`
      : `last tested ${shortWhen(h.lastTest.at)}, it didn’t work${h.lastOkAt ? ` (it last worked ${shortWhen(h.lastOkAt)})` : ''}`;
  return (
    <div className="key-health">
      <small className="muted" style={{ display: 'block' }}>
        {age ? `${age[0]!.toUpperCase()}${age.slice(1)}` : 'In EasyPanel, so its age isn’t known (move it into Stitch to keep track)'}; {tested}.
      </small>
      {h.rotateDue && (
        <Notice tone="warn" title="Time to replace the API token">
          It’s {h.ageDays} days old; replace it every {h.rotateEveryDays} days. Make a new token in Ahoi, Replace it here, Test connection, then delete the old one in Ahoi.
        </Notice>
      )}
    </div>
  );
}

function Copy({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      size="small"
      icon={done ? 'check' : 'copy'}
      aria-label={`Copy ${label}`}
      onClick={() =>
        void navigator.clipboard?.writeText(text).then(
          () => {
            setDone(true);
            window.setTimeout(() => setDone(false), 2000);
          },
          () => undefined
        )
      }
    >
      {done ? 'Copied' : 'Copy'}
    </Button>
  );
}

function AccountSettings({ account, onChanged }: { account: string; onChanged: () => void }) {
  const data = useData(() => carrierApi.get(account), [account]);
  const [view, setView] = useState<CarrierSettingsView | null>(null);
  const s = view ?? data.data ?? null;
  const [busy, setBusy] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [tokenOpen, setTokenOpen] = useState(false);
  const [token, setToken] = useState('');
  const [tried, setTried] = useState<string | null>(null);
  const [spendOpen, setSpendOpen] = useState(false);
  const [secretOpen, setSecretOpen] = useState(false);
  const [flows, setFlows] = useState<{ id: string; name: string; routes: string[] }[] | null>(null);
  const [trunks, setTrunks] = useState<{ id: string; name: string; companies: number }[] | null>(null);
  const [newIp, setNewIp] = useState('');
  const [url, setUrl] = useState<string | null>(null);
  const [test, setTest] = useState<string | null>(null);

  async function run(key: string, fn: () => Promise<CarrierSettingsView | void>, done?: string): Promise<boolean> {
    setBusy(key);
    setProblem(null);
    setNote(null);
    try {
      const next = await fn();
      if (next) setView(next);
      if (done) setNote(done);
      onChanged();
      return true;
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setBusy('');
    }
  }
  const save = (change: CarrierChange, done?: string) => run(Object.keys(change).join(','), () => carrierApi.save(change, account), done);

  if (data.error) {
    return (
      <Card title="Carrier account">
        <LoadError error={data.error} retry={data.reload} />
      </Card>
    );
  }
  if (!s) {
    return (
      <Card title="Carrier account">
        <Loading />
      </Card>
    );
  }

  const stillInEasyPanel = s.easypanel.filter((v) => !v.inStitch);
  const canDelete = s.easypanel.filter((v) => v.inStitch);
  const flowOptions = [
    { value: '', label: s.callFlow.value ? `Keep ${s.callFlow.value}` : 'Choose a call flow' },
    ...(flows ?? []).map((f) => ({ value: f.id, label: `${f.name} (${f.id})${f.routes.length ? ` → ${f.routes.join(', ')}` : ''}` }))
  ];
  const trunkOptions = [
    { value: '', label: s.trunk.value ? `Keep trunk ${s.trunk.value}` : 'Choose a trunk' },
    ...(trunks ?? []).map((t) => ({ value: t.id, label: `${t.name || 'Trunk'} (${t.id}) · ${t.companies === 1 ? '1 company' : `${t.companies} companies`}` }))
  ];

  const row = (title: string, body: ReactNode) => (
    <>
      <dt>{title}</dt>
      <dd>{body}</dd>
    </>
  );

  const main = s.account?.main ?? true;
  return (
    <Card title={`Carrier (Ahoi)${s.account && !(s.account.main && s.account.name === 'Main') ? `: ${s.account.name}` : ''}`}>
      <div className="card-body">
        <p className="muted" style={{ marginTop: 0 }}>
          Phone numbers, 911 addresses, moving numbers in, and texting. Changes here take effect in a few seconds, with no redeploy. Anything saved here is used instead of the same setting in EasyPanel.
        </p>
        {!s.canSeal && (
          <div style={{ marginBottom: 12 }}>
            <Notice tone="warn" title="Secrets can’t be saved here yet">
              Set DATA_ENCRYPTION_KEY in EasyPanel and redeploy once. It locks the API token and the webhook secret before they’re saved.
            </Notice>
          </div>
        )}
        {stillInEasyPanel.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Notice tone="neutral" title="Still set in EasyPanel">
              <p style={{ margin: '0 0 8px' }}>{stillInEasyPanel.map((v) => v.name).join(', ')}. Move them here once, then delete them from EasyPanel.</p>
              <Button
                variant="primary"
                icon="download"
                disabled={busy !== ''}
                onClick={() =>
                  void run('move', async () => {
                    const r = await carrierApi.move();
                    setNote(
                      [r.moved.length ? `Moved into Stitch: ${r.moved.join(', ')}.` : 'Nothing to move.', r.kept.length ? `${r.kept.join(' and ')} stay in EasyPanel until DATA_ENCRYPTION_KEY is set.` : '']
                        .filter(Boolean)
                        .join(' ')
                    );
                    return r.settings;
                  })
                }
              >
                {busy === 'move' ? 'Moving…' : 'Move from EasyPanel'}
              </Button>
            </Notice>
          </div>
        )}
        {canDelete.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Notice tone="ok" title="You can delete these from EasyPanel">
              {canDelete.map((v) => v.name).join(', ')}: Stitch uses its own now. Delete them in EasyPanel → Environment whenever you next redeploy.
            </Notice>
          </div>
        )}

        <dl className="dl">
          {row(
            'API token',
            <>
              {s.apiKey.set ? <Chip tone="ok">Set</Chip> : <Chip tone="warn">Not set</Chip>} {s.apiKey.unreadable && <Chip tone="crit">Saved, but can’t be opened with this DATA_ENCRYPTION_KEY</Chip>}
              <From source={s.apiKey.source} setting="apiKey" changed={s.apiKey.changed} />
              {s.apiKey.set && s.keyHealth && <KeyHealth h={s.keyHealth} />}
              <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                <Button size="small" icon="key" disabled={!s.canSeal || busy !== ''} onClick={() => (setToken(''), setTried(null), setTokenOpen(true))}>
                  {s.apiKey.set ? 'Replace' : 'Add'}
                </Button>
                {s.apiKey.source === 'stitch' && (
                  <Button size="small" variant="ghost" disabled={busy !== ''} onClick={() => void save({ apiKey: null }, 'The API token saved in Stitch is removed.')}>
                    Remove
                  </Button>
                )}
              </div>
            </>
          )}
          {row(
            'Spending',
            <>
              {s.writes.value ? <Chip tone="warn">On: buying, 911 changes and texts are real</Chip> : <Chip>Off: nothing is bought, changed or sent</Chip>}
              <From source={s.writes.source} setting="writes" changed={s.writes.changed} />
              <div style={{ marginTop: 6 }}>
                {s.writes.value ? (
                  <Button size="small" disabled={busy !== ''} onClick={() => void save({ writes: false }, 'Spending is off.')}>
                    Turn off
                  </Button>
                ) : (
                  <Button size="small" variant="primary" disabled={busy !== '' || !s.apiKey.set} onClick={() => setSpendOpen(true)}>
                    Turn on…
                  </Button>
                )}
              </div>
            </>
          )}
          {row(
            'Call flow for new numbers',
            <>
              {s.callFlow.value ? <span className="mono">{s.callFlow.value}</span> : <Chip tone="warn">Not chosen</Chip>}
              <From source={s.callFlow.source} setting="callFlow" changed={s.callFlow.changed} />
              {flows ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 6, flexWrap: 'wrap' }}>
                  <SelectField
                    label="Call flow"
                    options={flowOptions}
                    defaultValue=""
                    hint="The one that sends calls on to the phone system."
                    onChange={(e) => e.target.value && void save({ callFlow: e.target.value }, 'Call flow saved.')}
                  />
                </div>
              ) : (
                <div style={{ marginTop: 6 }}>
                  <Button
                    size="small"
                    disabled={!s.apiKey.set || busy !== ''}
                    onClick={() =>
                      void run('flows', async () => {
                        setFlows((await carrierApi.callFlows(account)).callFlows);
                      })
                    }
                  >
                    {busy === 'flows' ? 'Asking Ahoi…' : 'Choose from Ahoi’s list'}
                  </Button>
                </div>
              )}
            </>
          )}
          {main && row(
            'Phone system trunk for new numbers',
            <>
              {s.trunk.value ? <span className="mono">Trunk {s.trunk.value}</span> : <span>Each company’s most-used trunk</span>}
              <From source={s.trunk.source} setting="trunk" changed={s.trunk.changed} />
              {trunks ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 6, flexWrap: 'wrap' }}>
                  <SelectField label="Trunk" options={trunkOptions} defaultValue="" onChange={(e) => e.target.value && void save({ trunk: e.target.value }, 'Trunk saved.')} />
                  {s.trunk.source === 'stitch' && (
                    <Button size="small" variant="ghost" onClick={() => void save({ trunk: null }, 'Trunk choice removed.')}>
                      Use each company’s most-used
                    </Button>
                  )}
                </div>
              ) : (
                <div style={{ marginTop: 6 }}>
                  <Button
                    size="small"
                    disabled={busy !== ''}
                    onClick={() =>
                      void run('trunks', async () => {
                        setTrunks((await carrierApi.trunks()).trunks);
                      })
                    }
                  >
                    {busy === 'trunks' ? 'Asking the phone system…' : 'Choose from the phone system’s list'}
                  </Button>
                </div>
              )}
            </>
          )}
          {row(
            'Texting webhooks',
            <>
              {s.webhookSecret.set ? <Chip tone="ok">On</Chip> : <Chip tone="warn">Off: no webhook secret</Chip>} {s.webhookSecret.unreadable && <Chip tone="crit">Saved, but can’t be opened with this DATA_ENCRYPTION_KEY</Chip>}
              <From source={s.webhookSecret.source} setting="webhookSecret" changed={s.webhookSecret.changed} />
              {s.webhooks.inboundUrl && s.webhooks.receiptUrl ? (
                <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                  <div>
                    <small className="muted">In Ahoi → User Settings → Incoming SMS/MMS URL</small>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span className="mono" style={{ overflowWrap: 'anywhere', flex: 1 }}>
                        {s.webhooks.inboundUrl}
                      </span>
                      <Copy text={s.webhooks.inboundUrl} label="the incoming texts address" />
                    </div>
                  </div>
                  <div>
                    <small className="muted">In Ahoi → User Settings → Receive SMS/MMS Delivery Messages URL</small>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span className="mono" style={{ overflowWrap: 'anywhere', flex: 1 }}>
                        {s.webhooks.receiptUrl}
                      </span>
                      <Copy text={s.webhooks.receiptUrl} label="the delivery receipts address" />
                    </div>
                  </div>
                </div>
              ) : (
                s.webhookSecret.set && !s.webhooks.publicUrl && <small className="muted">Set PUBLIC_URL to see the addresses.</small>
              )}
              <div style={{ marginTop: 6 }}>
                <Button size="small" disabled={!s.canSeal || busy !== ''} onClick={() => (s.webhookSecret.set ? setSecretOpen(true) : void run('secret', () => carrierApi.newWebhookSecret(account), 'Webhooks are on. Paste both addresses into Ahoi.'))}>
                  {s.webhookSecret.set ? 'Make a new secret…' : 'Turn on (make a secret)'}
                </Button>
              </div>
            </>
          )}
          {row(
            'Ahoi’s addresses',
            <>
              {s.webhookIps.value.length ? (
                <ul className="plain-list" aria-label="Addresses allowed to call the webhooks">
                  {s.webhookIps.value.map((ip) => (
                    <li key={ip} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span className="mono grow">{ip}</span>
                      <Button size="small" variant="ghost" aria-label={`Remove ${ip}`} disabled={busy !== ''} onClick={() => void save({ webhookIps: s.webhookIps.value.filter((x) => x !== ip) })}>
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <span>Any address (with the secret)</span>
              )}
              <From source={s.webhookIps.source} setting="webhookIps" changed={s.webhookIps.changed} />
              {s.webhooks.seen.filter((ip) => !s.webhookIps.value.includes(ip)).length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <small className="muted">Called from lately:</small>
                  {s.webhooks.seen
                    .filter((ip) => !s.webhookIps.value.includes(ip))
                    .map((ip) => (
                      <Button key={ip} size="small" icon="plus" disabled={busy !== ''} onClick={() => void save({ webhookIps: [...s.webhookIps.value, ip] }, `${ip} added.`)}>
                        {ip}
                      </Button>
                    ))}
                </div>
              )}
              <form
                style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 6, flexWrap: 'wrap' }}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (newIp.trim()) void save({ webhookIps: [...s.webhookIps.value, newIp.trim()] }).then((ok) => ok && setNewIp(''));
                }}
              >
                <Field label="Add an address" placeholder="203.0.113.10 or 198.51.100.0/24" value={newIp} onChange={(e) => setNewIp(e.target.value)} />
                <Button size="small" type="submit" disabled={!newIp.trim() || busy !== ''}>
                  Add
                </Button>
              </form>
            </>
          )}
        </dl>

        <details style={{ marginTop: 12 }}>
          <summary>Advanced</summary>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 8, flexWrap: 'wrap' }}>
            <Field label="API address" value={url ?? s.apiUrl.value} onChange={(e) => setUrl(e.target.value)} hint={<From source={s.apiUrl.source} setting="apiUrl" changed={s.apiUrl.changed} />} />
            <Button size="small" disabled={url === null || url === s.apiUrl.value || busy !== ''} onClick={() => void save({ apiUrl: url }, 'API address saved.').then((ok) => ok && setUrl(null))}>
              Save
            </Button>
            {s.apiUrl.source === 'stitch' && (
              <Button size="small" variant="ghost" onClick={() => void save({ apiUrl: null }, 'Using Ahoi’s own address.').then((ok) => ok && setUrl(null))}>
                Use Ahoi’s own
              </Button>
            )}
          </div>
        </details>

        {problem && (
          <div style={{ marginTop: 12 }}>
            <Notice tone="crit">{problem}</Notice>
          </div>
        )}
        {note && (
          <div style={{ marginTop: 12 }} role="status">
            <Notice tone="ok">{note}</Notice>
          </div>
        )}
        {test && (
          <div style={{ marginTop: 12 }} role="status">
            <Notice tone={test.startsWith('Ahoi answered') ? 'ok' : 'crit'}>{test}</Notice>
          </div>
        )}
      </div>
      <div className="card-foot">
        <Button
          icon="refresh"
          disabled={!s.apiKey.set || busy !== ''}
          onClick={() =>
            void run('test', async () => {
              const r = await carrierApi.test({}, account);
              setTest(r.ok ? `Ahoi answered in ${r.ms} ms: ${r.callFlows === 1 ? '1 call flow' : `${r.callFlows} call flows`}, ${r.numbers === 1 ? '1 number' : `${r.numbers} numbers`}.` : r.message);
              // The token's last test, now on the page (3125).
              return carrierApi.get(account);
            })
          }
        >
          {busy === 'test' ? 'Testing…' : 'Test connection'}
        </Button>
        {s.updatedAt && (
          <span className="muted" style={{ fontSize: 13 }}>
            Last changed {shortWhen(s.updatedAt)}
            {s.updatedByName ? ` by ${s.updatedByName}` : ''}
          </span>
        )}
      </div>

      <Dialog
        open={tokenOpen}
        onClose={() => setTokenOpen(false)}
        title={s.apiKey.set ? 'Replace the API token' : 'Add the API token'}
        subtitle="From Ahoi’s portal. Once saved it’s locked away and never shown again."
        foot={
          <>
            <Button onClick={() => setTokenOpen(false)}>Cancel</Button>
            <Button
              disabled={!token.trim() || busy !== ''}
              onClick={() =>
                void run('try', async () => {
                  const r = await carrierApi.test({ apiKey: token.trim() }, account);
                  setTried(r.ok ? `It works: ${r.callFlows === 1 ? '1 call flow' : `${r.callFlows} call flows`}, ${r.numbers === 1 ? '1 number' : `${r.numbers} numbers`}.` : r.message);
                })
              }
            >
              {busy === 'try' ? 'Trying…' : 'Try it'}
            </Button>
            <Button
              variant="primary"
              disabled={!token.trim() || busy !== ''}
              onClick={() =>
                void save({ apiKey: token.trim() }, 'API token saved.').then((ok) => {
                  if (ok) {
                    setToken('');
                    setTokenOpen(false);
                  }
                })
              }
            >
              Save
            </Button>
          </>
        }
      >
        <Field label="API token" type="password" autoComplete="off" spellCheck={false} value={token} onChange={(e) => setToken(e.target.value)} />
        {tried && <Notice tone={tried.startsWith('It works') ? 'ok' : 'crit'}>{tried}</Notice>}
      </Dialog>

      <Dialog
        open={spendOpen}
        onClose={() => setSpendOpen(false)}
        title="Turn spending on?"
        foot={
          <>
            <Button onClick={() => setSpendOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={busy !== ''}
              onClick={() =>
                void save({ writes: true, confirmSpending: true }, 'Spending is on.').then((ok) => {
                  if (ok) setSpendOpen(false);
                })
              }
            >
              Turn spending on
            </Button>
          </>
        }
      >
        <p style={{ marginTop: 0 }}>Stitch will be able to buy and release numbers, change 911 addresses and caller names, submit number moves, and send texts. These are real and cost money.</p>
        <p className="muted" style={{ marginBottom: 0 }}>
          Companies still need their own limits to order numbers, and the test center never spends money. Who turned it on goes in Activity.
        </p>
      </Dialog>

      <Dialog
        open={secretOpen}
        onClose={() => setSecretOpen(false)}
        title="Make a new webhook secret?"
        foot={
          <>
            <Button onClick={() => setSecretOpen(false)}>Cancel</Button>
            <Button
              variant="danger"
              disabled={busy !== ''}
              onClick={() =>
                void run('secret', () => carrierApi.newWebhookSecret(account), 'New secret made. Paste both new addresses into Ahoi now.').then((ok) => {
                  if (ok) setSecretOpen(false);
                })
              }
            >
              Make a new secret
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>The addresses Ahoi has now stop working at once. Texts and delivery receipts are turned away until you paste the two new addresses into Ahoi’s User Settings.</p>
      </Dialog>
    </Card>
  );
}

/**
 * System → Carrier: Stitch's carrier accounts (1.12.25), then the chosen one's settings. With one
 * account the bar is just a line saying so, and "Add an account".
 */
export function CarrierCard() {
  const list = useData(() => carrierApi.accounts(), []);
  const [rows, setRows] = useState<CarrierAccountRow[] | null>(null);
  const accounts = rows ?? list.data?.accounts ?? null;
  const [chosen, setChosen] = useState('main');
  const [busy, setBusy] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [name, setName] = useState('');

  async function act(key: string, fn: () => Promise<{ accounts: CarrierAccountRow[] }>): Promise<boolean> {
    setBusy(key);
    setProblem(null);
    try {
      setRows((await fn()).accounts);
      return true;
    } catch (e) {
      setProblem(e instanceof ApiError || e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setBusy('');
    }
  }
  const refresh = () => void carrierApi.accounts().then((r) => setRows(r.accounts), () => undefined);

  if (list.error) {
    return (
      <Card title="Carrier accounts">
        <LoadError error={list.error} retry={list.reload} />
      </Card>
    );
  }
  if (!accounts) {
    return (
      <Card title="Carrier accounts">
        <Loading />
      </Card>
    );
  }
  const current = accounts.find((a) => a.id === chosen) ?? accounts[0]!;
  const several = accounts.length > 1;

  return (
    <>
      <Card title="Carrier accounts">
        <div className="card-body">
          <p className="muted" style={{ marginTop: 0 }}>
            {several
              ? 'Stitch finds which account each number is on by itself: its texts, 911 address and routing go through that account. New numbers and moves go to the account marked New numbers.'
              : 'One Ahoi account. If your numbers are spread over several, add the others: Stitch then finds which account each number is on by itself.'}
          </p>
          {problem && <Notice tone="crit">{problem}</Notice>}
          <div role="tablist" aria-label="Carrier accounts" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {accounts.map((a) => (
              <Button key={a.id} role="tab" aria-selected={a.id === current.id} variant={a.id === current.id ? 'secondary' : 'ghost'} size="small" onClick={() => setChosen(a.id)}>
                {a.name}
                {a.isDefault && several && (
                  <>
                    {' '}
                    <Chip tone="ok">New numbers</Chip>
                  </>
                )}
                {!a.token && (
                  <>
                    {' '}
                    <Chip tone="warn">No token</Chip>
                  </>
                )}
                {a.problem && (
                  <>
                    {' '}
                    <Chip tone="crit">Can’t read its numbers</Chip>
                  </>
                )}
              </Button>
            ))}
          </div>
          {current.problem && (
            <Notice tone="warn">
              The numbers on {current.name} couldn’t be read ({shortWhen(current.problem.at)}): {current.problem.message}
            </Notice>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              size="small"
              icon="plus"
              disabled={busy !== ''}
              onClick={() => {
                setName('');
                setAdding(true);
              }}
            >
              Add an account
            </Button>
            <Button
              size="small"
              variant="ghost"
              disabled={busy !== ''}
              onClick={() => {
                setName(current.name);
                setRenaming(true);
              }}
            >
              Rename {current.name}
            </Button>
            {several && !current.isDefault && (
              <Button size="small" variant="ghost" disabled={busy !== '' || !current.token} title={current.token ? undefined : 'Add its API token first'} onClick={() => void act('default', () => carrierApi.editAccount(current.id, { default: true }))}>
                Use {current.name} for new numbers
              </Button>
            )}
            {!current.main && !current.isDefault && (
              <Button size="small" variant="ghost" disabled={busy !== ''} onClick={() => setRemoving(true)}>
                Remove {current.name}
              </Button>
            )}
          </div>
        </div>
      </Card>
      <AccountSettings key={current.id} account={current.id} onChanged={refresh} />
      <Dialog
        open={adding || renaming}
        onClose={() => {
          setAdding(false);
          setRenaming(false);
        }}
        title={adding ? 'Add a carrier account' : `Rename ${current.name}`}
        subtitle={adding ? 'Then add its API token, call flow and webhooks below, as for the first one.' : undefined}
        foot={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setAdding(false);
                setRenaming(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!name.trim() || busy !== ''}
              onClick={() =>
                void (async () => {
                  if (adding) {
                    setBusy('add');
                    setProblem(null);
                    try {
                      const r = await carrierApi.addAccount(name.trim());
                      setRows(r.accounts);
                      setChosen(r.added);
                      setAdding(false);
                    } catch (e) {
                      setProblem(e instanceof Error ? e.message : String(e));
                    } finally {
                      setBusy('');
                    }
                  } else if (await act('rename', () => carrierApi.editAccount(current.id, { name: name.trim() }))) setRenaming(false);
                })()
              }
            >
              {adding ? 'Add it' : 'Save'}
            </Button>
          </>
        }
      >
        <Field label="Name" value={name} maxLength={40} placeholder="Baltimore numbers" onChange={(e) => setName(e.target.value)} hint="Only Stitch staff see it." />
        {problem && <Notice tone="crit">{problem}</Notice>}
      </Dialog>
      <Dialog
        open={removing}
        onClose={() => setRemoving(false)}
        title={`Remove ${current.name}?`}
        foot={
          <>
            <Button variant="ghost" onClick={() => setRemoving(false)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              disabled={busy !== ''}
              onClick={() =>
                void act('remove', () => carrierApi.removeAccount(current.id)).then((ok) => {
                  if (ok) {
                    setRemoving(false);
                    setChosen('main');
                  }
                })
              }
            >
              Remove it
            </Button>
          </>
        }
      >
        <p style={{ marginTop: 0 }}>
          Its API token and settings are forgotten, and its webhook addresses stop working. Numbers still on that Ahoi account can’t be texted from or changed in Stitch until it’s added again.
        </p>
        {problem && <Notice tone="crit">{problem}</Notice>}
      </Dialog>
    </>
  );
}
